import { NON_MOVE_REPORT_MODE } from '@racemap/sdk/schema/trackers';
import { RacemapColors } from '@racemap/utilities/consts/common';
import { formatSeconds } from '@racemap/utilities/formatting';
import type { TableCellProps } from 'react-virtualized';
import { getConcatenatedKey } from 'src/components/utils/getConcatenatedKey';
import {
  type ManagedTracker,
  TrackerColumnKeys,
} from '../../../../store/trackers/trackers_reducers';
import { Columns } from '../../../DataTable';
import { IconFailCircle } from '../../../Icon';

interface CellProps extends TableCellProps {
  cellData?: ManagedTracker['state'];
  rowData: ManagedTracker;
}

export const NonMovementColumn = () => {
  const cellRenderer = ({ rowData }: CellProps) => {
    const {
      nonMoveCheckInterval,
      nonMoveSendInterval,
      nmdReportMode,
      nonMovementModeDisabled,
      adjustIntervalsDuringNonMovement,
    } = rowData.setting ?? {};
    if (
      (nonMoveCheckInterval == null && nonMoveSendInterval == null && nmdReportMode == null) ||
      nonMovementModeDisabled
    ) {
      return <IconFailCircle color={RacemapColors.BaseRed} />;
    }
    const formattedNonMoveCheckInterval = adjustIntervalsDuringNonMovement
      ? formatSeconds(nonMoveCheckInterval)
      : '--';

    const formattedNonMoveSendInterval = adjustIntervalsDuringNonMovement
      ? formatSeconds(nonMoveSendInterval)
      : '--';

    const reportModeLabels = {
      [NON_MOVE_REPORT_MODE.LAST_FIX]: 'L-Fix',
      [NON_MOVE_REPORT_MODE.CURRENT_LOCATION]: 'C-Loc',
      [NON_MOVE_REPORT_MODE.LAST_FIX_AND_CURRENT_LOCATION]: 'L-Fix & C-Loc',
    };

    const selectedReportMode = rowData.setting?.nmdReportMode ?? null;
    const reportModeLabel =
      selectedReportMode != null
        ? reportModeLabels[selectedReportMode as NON_MOVE_REPORT_MODE]
        : '--';

    return (
      <div>
        {formattedNonMoveCheckInterval} / {formattedNonMoveSendInterval} / {reportModeLabel}
      </div>
    );
  };

  return Columns.sortingColumn({
    dataKey: TrackerColumnKeys.NON_MOVEMENT,
    label: 'Non-Movement',
    hint: 'Displays non-movement settings, including how often the tracker checks and sends updates when stationary, and the report mode: Last Fix, Current Location, or both.',
    cellRenderer,
    width: 170,
    minWidth: 170,
    columnData: { hiddenOnDefault: true },
    sortingMethod: (_cA, _cB, rowDataA: ManagedTracker, rowDataB: ManagedTracker) => {
      const keyA = getConcatenatedKey([
        rowDataA.setting?.nonMoveCheckInterval,
        rowDataA.setting?.nonMoveSendInterval,
        rowDataA.setting?.nmdReportMode,
      ]);
      const keyB = getConcatenatedKey([
        rowDataB.setting?.nonMoveCheckInterval,
        rowDataB.setting?.nonMoveSendInterval,
        rowDataB.setting?.nmdReportMode,
      ]);

      return keyA.localeCompare(keyB);
    },
  });
};
